import * as React from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/src/stylesheets/datepicker.scss"
import moment from "moment"

import TextInput, { ITextInputProps } from "../TextInput"
import { userInfoActions } from "../../actions"
import { useDispatch, useSelector } from "react-redux"
import { isRequired, checkDateNotOverTodayDate } from "../../utils/validators"
import * as AppTypes from "AppTypes"

import "./dateInput.scss"
import { classNames } from "../../utils"

const dateFormatRegex = /(^\d{1,2}$)|(^\d{2}\/$)|(^\d{2}\/\d{1,2}$)|(^\d{2}\/\d{2}\/$)|(^\d{2}\/\d{2}\/\d{1,4}$)/;
const regex = /(^\d{2}$)|(^\d{2}\/\d{2}$)/;

interface IDateOfBirthFieldDesktopProps {
    cluster: ITextInputProps["cluster"]
    disabled?: boolean
}

const DateOfBirthFieldDesktop: React.FC<IDateOfBirthFieldDesktopProps> = props => {
    const dispatch = useDispatch()
    const { disabled, cluster } = props
    const { userInfo, langPack } = useSelector((store: AppTypes.ReducerState) => ({
        userInfo: store.userInfo,
        langPack: store.config.langPack
    }))

    const wrapperElem = React.useRef<HTMLDivElement>(null)
    const [isFocused, setFocus] = React.useState(false)
    const [forceDate, setForceDate] = React.useState({ value: "" })

    const handleUpdateUserInfo: ITextInputProps["onValidation"] = (validation) => {
        const { isValid, inputId, value } = validation

        if (isValid) {
            dispatch(userInfoActions.updateUserInfo({ [inputId]: value.split("/").reverse().join("-") }))
        } else {
            dispatch(userInfoActions.updateUserInfo({ [inputId]: "" }))
        }
    }

    return (
        <div
            className={classNames({
                "app-date-input-desktop": true,
                "app-date-input-desktop--focused": isFocused
            })}
            tabIndex={0}
            onFocus={() => !disabled && setFocus(true)}
            onBlur={() => setFocus(false)}
            ref={wrapperElem}
        >
            <TextInput
                disabled={disabled}
                inputLabel={langPack["labelDateOfBirth"]}
                initValue={userInfo.dateOfBirth ? moment(userInfo.dateOfBirth).format("DD/MM/YYYY") : ""}
                forceUpdate={forceDate}
                inputId={"dateOfBirth"}
                validators={[isRequired,
                    (value) => checkDateNotOverTodayDate(value.split("/").reverse().join("-"))]
                }
                cluster={cluster}
                onValidation={handleUpdateUserInfo}
                placeholder={"DD/MM/YYYY"}
                autocomplete="off"

                typingHandler={(value, keyCode, prevValue) => {
                    const dateArray = value.split("/")

                    if (dateArray.length <= 2 && !!dateArray[0]) {
                        // inputting day
                        if (dateArray[0].length === 2 || keyCode === 191) {
                            if (Number(dateArray[0]) > 31) dateArray[0] = "31"
                            if (Number(dateArray[0]) < 1) dateArray[0] = "01"
                            if (Number(dateArray[0]) < 10) dateArray[0] = "0" + Number(dateArray[0])
                        }
                    }

                    if (dateArray.length >= 2 && dateArray.length <= 3 && !!dateArray[1]) {
                        // inputting day
                        if (dateArray[1].length === 2 || keyCode === 191) {
                            if (Number(dateArray[1]) > 12) dateArray[1] = "12"
                            if (Number(dateArray[1]) < 1) dateArray[1] = "01"
                            if (Number(dateArray[1]) < 10) dateArray[1] = "0" + Number(dateArray[1])
                        }
                    }
                    value = dateArray.join("/")
                    let val = (regex.test(value) && keyCode !== 8)
                        ? `${value}${"/"}`
                        : value;

                    if (dateFormatRegex.test(val) || val === "") {
                        return val
                    } else {
                        return prevValue
                    }

                }}
            />

            <div className="date-picker-wrapper">
                <DatePicker
                    selected={userInfo.dateOfBirth ? new Date(userInfo.dateOfBirth) : new Date()}
                    onChange={(value: Date) => {
                        setForceDate({
                            value: moment(value ? value : new Date()).format("DD/MM/YYYY")
                        })

                        const dateOfBirth = moment(value).format("YYYY-MM-DD")
                        dispatch(userInfoActions.updateUserInfo({ dateOfBirth }))
                        wrapperElem.current && wrapperElem.current.blur()

                        setFocus(false)
                    }}
                    dropdownMode="select"
                    showMonthDropdown
                    showYearDropdown
                    inline
                />
            </div>
        </div>
    )
}

export default DateOfBirthFieldDesktop
